import { Box, Divider, Stack, TextField, Typography } from "@mui/material";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { AnalyzingProductBlock } from "../AnalyzingProductBlock";
import { toast } from "react-toastify";
import { CustomToast } from "../../ui/CustomToast";
import { URL_REGEX } from "../../../utils/regex";
import { Fields } from "./ProductDetailsModal";
import { FileList } from "../../ui";
import { FileDropZone } from "../../ui";

type Props = {
  campaign_id: string;
  setErrorHandler: (field: string, value: boolean) => void;
  pdfFiles: File[];
  setPdfFiles: React.Dispatch<React.SetStateAction<File[]>>;
  productUrl: string;
  setProductUrl: (url: string) => void;
  setResultHandler: (result: Fields) => void;
  initProductUrl: string;
};

export const ImportedDataBlock = ({
  campaign_id,
  setErrorHandler,
  setPdfFiles,
  pdfFiles,
  productUrl,
  setProductUrl,
  setResultHandler,
  initProductUrl,
}: Props) => {
  const [isAnalysis, setIsAnalysis] = useState<boolean>(false);
  const [productUrlError, setProductUrlError] = useState<boolean>(false);
  const fileToReplaceRef = useRef<File | null>(null);

  const setResultDataHandler = (result: Fields) => {
    if (result.error) {
      const { files, url } = result.error;

      toast.error(
        <CustomToast
          type={"error"}
          text={
            "The content of the attached PDF file(s) doesn’t match the provided URL or other attached PDFs. \n" +
            "Please review the file(s) before  proceeding."
          }
          title={"Attachment Failed"}
        />,
      );

      files &&
        setPdfFiles((state) =>
          state.filter(({ name }) => !files.includes(name)),
        );

      url && setProductUrl("");

      return;
    }

    setResultHandler(result);
  };

  useEffect(() => {
    if (URL_REGEX.test(productUrl)) {
      setProductUrlError(false);
      setErrorHandler("url", false);
    } else {
      setProductUrlError(true);
      setErrorHandler("url", true);
    }

    if (!productUrl) {
      setProductUrlError(false);
      setErrorHandler("url", false);
    }
  }, [productUrl]);

  const urlInputHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setProductUrl(e.target.value);
  };

  const uploadFileHandler = (files: File[]) => {
    const fileToReplace = fileToReplaceRef.current;
    fileToReplaceRef.current = null;

    const wrongFileUploaded = files.some(
      (file) => file.type !== "application/pdf",
    );

    if (wrongFileUploaded) {
      return toast.error(
        <CustomToast
          type={"error"}
          text={
            "You have selected an unsupported file extension. Please select a .pdf file and try again."
          }
          title={"File Upload Failed"}
        />,
      );
    }

    const updatedFiles = [...pdfFiles, ...files].filter(
      (file) => file.name !== fileToReplace?.name,
    );

    setPdfFiles(updatedFiles);
  };

  const removeFileHandler = (fileName: string) => {
    setPdfFiles(pdfFiles.filter((file) => file.name !== fileName));
  };

  const changeFileHandler = (fileName: string, file: File) => {
    setPdfFiles((state) =>
      state.map((el) => {
        if (el.name !== fileName) return el;
        return file;
      }),
    );
  };

  return (
    <Stack gap={1} pt="8px" pb="32px">
      <Stack direction="row" gap="8px">
        <TextField
          disabled={isAnalysis}
          helperText={
            productUrlError && (
              <Stack>
                <span>Invalid URL: Please check the format and try again.</span>
                <span>Example: https://www.example.com</span>
              </Stack>
            )
          }
          error={productUrlError}
          fullWidth
          label="Product, Solution or Service Page URL"
          value={productUrl}
          onChange={urlInputHandler}
          placeholder="www.example.com"
          sx={{
            "& .MuiInputBase-root.Mui-disabled": {
              pointerEvents: "none",
              backgroundColor: "#f5f5f5",
            },
          }}
        />
      </Stack>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Divider sx={{ flexGrow: 1, color: "#CFCFCF" }} />
        <Typography
          variant="button"
          sx={{
            marginX: "8px",
            color: "#4C545B",
          }}
        >
          or / and
        </Typography>
        <Divider sx={{ flexGrow: 1, color: "#CFCFCF" }} />
      </Box>

      <Stack gap="16px">
        <FileList
          isDisabled={isAnalysis}
          files={pdfFiles}
          removeFileHandler={removeFileHandler}
          changeFileHandler={changeFileHandler}
          mimeTypes={["application/pdf"]}
          acceptedExtensions={["pdf"]}
        />

        <FileDropZone
          isDisabled={isAnalysis}
          uploadedFiles={pdfFiles}
          onFileUpload={uploadFileHandler}
          acceptedExtensions={["pdf"]}
          mimeTypes={["application/pdf"]}
          label="Drag PDF files with your offering description here"
        />
        {(pdfFiles.length > 0 ||
          (productUrl !== initProductUrl && productUrl)) &&
          !productUrlError && (
            <Stack alignItems="center" justifyContent="center" pt={2}>
              <AnalyzingProductBlock
                productUrl={productUrl}
                pdfFiles={pdfFiles}
                campaign_id={campaign_id}
                setResultHandler={setResultDataHandler}
                setIsAnalysis={setIsAnalysis}
              />
            </Stack>
          )}
      </Stack>
    </Stack>
  );
};
